/**
 * Бюджет-график оплат по неделям (БМ-12): поступления и платежи по условиям договоров рядом с планом оплат
 * разделов бюджета. Поток платежей считает ДДС проекта, здесь только сведение.
 */
import type { PaymentBudgetView } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { date, tengeShort } from '../../lib/format';
import { ErrorBox, Spinner } from '../../ui/feedback';
import { Panel } from '../../ui/layout';

export function PaymentBudgetPanel({ projectId, versionId }: { projectId: string; versionId: string }) {
  const q = useQuery({
    queryKey: ['budget', 'payment-budget', projectId, versionId],
    queryFn: () => api<PaymentBudgetView>(`/projects/${projectId}/payment-budget`, { query: { versionId } }),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const v = q.data;
  const t = v.totals;
  const empty =
    !t.plan &&
    !t.income &&
    !v.outside.income &&
    !v.outside.expense &&
    !t.expense &&
    !v.undated.income &&
    !v.undated.expense;
  if (empty) return null;
  const rows = v.weeks.map((w, i) => ({ w, i })).filter(({ i }) => v.plan[i] || v.income[i] || v.expense[i]);
  return (
    <Panel
      title="График оплат по неделям"
      meta={`БМ-12: поступления ${tengeShort(t.income)}, платежи ${tengeShort(t.expense)}, план бюджета ${tengeShort(t.plan)}`}
      bodyClassName="p-0"
    >
      {v.undated.income || v.undated.expense || v.outside.income || v.outside.expense ? (
        <p className="border-b border-border px-4 py-2 text-sm text-muted">
          Не попали в недели: без даты — поступления {tengeShort(v.undated.income)}, платежи{' '}
          {tengeShort(v.undated.expense)}; вне показанных недель — поступления {tengeShort(v.outside.income)},
          платежи {tengeShort(v.outside.expense)}.
        </p>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted">
              <th className="px-4 py-2 font-medium">Неделя с</th>
              <th className="px-3 py-2 text-right font-medium">План бюджета</th>
              <th className="px-3 py-2 text-right font-medium">Поступления</th>
              <th className="px-3 py-2 text-right font-medium">Платежи</th>
              <th className="px-3 py-2 text-right font-medium">Итог недели</th>
              <th className="px-4 py-2 text-right font-medium">Нарастающим</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ w, i }) => (
              <tr key={w} className="border-b border-border last:border-0">
                <td className="px-4 py-2 whitespace-nowrap">{date(w)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{tengeShort(v.plan[i])}</td>
                <td className="px-3 py-2 text-right tabular-nums">{tengeShort(v.income[i])}</td>
                <td className="px-3 py-2 text-right tabular-nums">{tengeShort(v.expense[i])}</td>
                <td className="px-3 py-2 text-right tabular-nums">{tengeShort(v.net[i])}</td>
                <td className="px-4 py-2 text-right tabular-nums">{tengeShort(v.cumulative[i])}</td>
              </tr>
            ))}
            <tr className="font-medium">
              <td className="px-4 py-2">Всего</td>
              <td className="px-3 py-2 text-right tabular-nums">{tengeShort(t.plan)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{tengeShort(t.income)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{tengeShort(t.expense)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{tengeShort(t.net)}</td>
              <td className="px-4 py-2" />
            </tr>
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
